// Client helpers for the community layer (likes, comments, follows,
// notifications). Public reads go through SECURITY DEFINER RPCs; writes are
// direct, RLS-gated table ops.
import { sb } from './supabase'

// ── Likes ─────────────────────────────────────────────────────────────────
export async function getDeckSocial(deckId) {
  const { data, error } = await sb.rpc('get_deck_social', { p_deck_id: deckId })
  if (error) throw error
  return data || null   // { like_count, comment_count, viewer_liked, is_owner }
}

export async function setDeckLike(deckId, userId, like) {
  if (like) {
    const { error } = await sb.from('deck_likes').upsert({ deck_id: deckId, user_id: userId }, { onConflict: 'deck_id,user_id', ignoreDuplicates: true })
    if (error) throw error
  } else {
    const { error } = await sb.from('deck_likes').delete().eq('deck_id', deckId).eq('user_id', userId)
    if (error) throw error
  }
}

// ── Comments ──────────────────────────────────────────────────────────────
export async function getDeckComments(deckId) {
  const { data, error } = await sb.rpc('get_deck_comments', { p_deck_id: deckId })
  if (error) throw error
  return data || []
}

export async function postComment(deckId, userId, body) {
  const trimmed = String(body || '').trim().slice(0, 2000)
  if (!trimmed) return
  const { error } = await sb.from('deck_comments').insert({ deck_id: deckId, user_id: userId, body: trimmed })
  if (error) throw error
}

export async function deleteComment(commentId) {
  const { error } = await sb.from('deck_comments').delete().eq('id', commentId)
  if (error) throw error
}

// ── Follows ───────────────────────────────────────────────────────────────
export async function getUserFollowStats(username) {
  const { data, error } = await sb.rpc('get_user_follow_stats', { p_username: username })
  if (error) throw error
  return data || null   // { user_id, follower_count, following_count, viewer_following, is_self }
}

export async function setFollow(followerId, followingId, follow) {
  if (follow) {
    const { error } = await sb.from('user_follows').upsert({ follower_id: followerId, following_id: followingId }, { onConflict: 'follower_id,following_id', ignoreDuplicates: true })
    if (error) throw error
  } else {
    const { error } = await sb.from('user_follows').delete().eq('follower_id', followerId).eq('following_id', followingId)
    if (error) throw error
  }
}

// ── Discovery ─────────────────────────────────────────────────────────────
export async function listPublicDecks({ sort = 'popular', format = null, limit = 24, offset = 0 } = {}) {
  const { data, error } = await sb.rpc('list_public_decks', { p_sort: sort, p_format: format, p_limit: limit, p_offset: offset })
  if (error) throw error
  return data || []
}

// ── Notifications ─────────────────────────────────────────────────────────
export async function getMyNotifications(limit = 30) {
  const { data, error } = await sb.rpc('get_my_notifications', { p_limit: limit })
  if (error) throw error
  return data || []
}

// `limit(0)` rather than `head: true`: same single request and the count still
// rides the Content-Range header, but Chrome logs every bodiless HEAD response
// as "Fetch failed loading" in the console even on a 200.
export async function getUnreadNotificationCount() {
  const { count, error } = await sb
    .from('notifications')
    .select('id', { count: 'exact' })
    .eq('read', false)
    .limit(0)
  if (error) throw error
  return count || 0
}

export async function markAllNotificationsRead() {
  const { error } = await sb.rpc('mark_all_notifications_read')
  if (error) throw error
}

// Milestone unlocks are detected client-side, so unlike social notifications
// the client writes these rows itself (RLS only lets it insert milestone rows
// addressed to itself). `ignoreDuplicates` + the (user_id, milestone_id) unique
// index means two devices spotting the same unlock produce one notification.
export async function recordMilestoneNotifications(userId, milestoneIds) {
  if (!userId || !milestoneIds?.length) return
  const rows = milestoneIds.map(id => ({ user_id: userId, type: 'milestone', milestone_id: id }))
  const { error } = await sb.from('notifications')
    .upsert(rows, { onConflict: 'user_id,milestone_id', ignoreDuplicates: true })
  if (error) throw error
}

/**
 * Release announcements, written the same way and deduped by the same index —
 * see src/lib/announcements.js. The type differs so the bell can render a
 * release note as a release note rather than as "Milestone unlocked".
 */
export async function recordAnnouncementNotifications(userId, announcementIds) {
  if (!userId || !announcementIds?.length) return
  const rows = announcementIds.map(id => ({ user_id: userId, type: 'announcement', milestone_id: id }))
  const { error } = await sb.from('notifications')
    .upsert(rows, { onConflict: 'user_id,milestone_id', ignoreDuplicates: true })
  if (error) throw error
}

/**
 * Price-move alerts, written the same way and deduped by the same index. Keys
 * are `price:<scryfall_id>:<date>:<finish>`, so a card moving on two days
 * produces two alerts and two devices seeing one move produce one.
 */
export async function recordPriceAlertNotifications(userId, alertKeys) {
  if (!userId || !alertKeys?.length) return
  const rows = alertKeys.map(id => ({ user_id: userId, type: 'price_alert', milestone_id: id }))
  const { error } = await sb.from('notifications')
    .upsert(rows, { onConflict: 'user_id,milestone_id', ignoreDuplicates: true })
  if (error) throw error
}

/** Removes one notification. Scoped by user as well as by RLS, as below. */
export async function deleteNotification(userId, id) {
  if (!userId || !id) return
  const { error } = await sb.from('notifications').delete().eq('user_id', userId).eq('id', id)
  if (error) throw error
}

/**
 * Deletes this user's notifications.
 *
 * `before` (an ISO timestamp) clears only rows older than it; without it the
 * lot goes. RLS already restricts deletes to the caller's own rows, so no
 * user_id filter is needed here — but one is passed anyway so a misconfigured
 * policy cannot turn this into a global wipe.
 *
 * Milestone and announcement rows are deleted too, and both watchers will
 * happily write them again on the next load: the dedupe index is the only
 * memory either of them has. That is the right trade for a "clear" button —
 * the alternative is a tombstone table to remember what was dismissed.
 */
export async function clearNotifications(userId, { before = null } = {}) {
  if (!userId) return 0
  let query = sb.from('notifications').delete().eq('user_id', userId)
  if (before) query = query.lt('created_at', before)
  const { error, count } = await query.select('id', { count: 'exact' })
  if (error) throw error
  return count ?? 0
}

/** Ids of milestone/announcement rows already recorded for this user. */
export async function fetchRecordedKeys(userId) {
  if (!userId) return new Set()
  const { data, error } = await sb.from('notifications')
    .select('milestone_id')
    .eq('user_id', userId)
    .not('milestone_id', 'is', null)
  if (error) throw error
  return new Set((data || []).map(r => r.milestone_id))
}
